import { useState, useRef, useEffect } from 'react';

export default function BookingsSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => data.append(key, val as string));
    try {
      const res = await fetch('https://readdy.ai/api/form/d79u422dqn9mib2trc0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); setCharCount(0); }
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* Left — Info */}
          <div className="w-full lg:w-[40%] reveal">
            <div className="inline-flex items-center gap-2 border border-gold/50 text-gold rounded-full px-4 py-1.5 mb-5">
              <i className="ri-mail-send-line text-sm w-4 h-4 flex items-center justify-center" />
              <span className="font-sans text-xs tracking-widest uppercase">Investment Inquiry</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-5">
              Express Your<br /><em>Interest</em>
            </h2>
            <p className="font-sans text-sm text-gray-600 leading-relaxed mb-7">
              To receive the full due diligence package, schedule a property visit, or discuss terms, please complete this confidential inquiry form. All submissions are handled with strict discretion.
            </p>

            <div className="space-y-3">
              {[
                { icon: 'ri-map-pin-line', label: 'Location', value: 'Matagalpa, Nicaragua · 3 blocks from Parque Morazán', href: null },
                { icon: 'ri-mail-line', label: 'Email', value: 'info@montebrisa.com', href: 'mailto:info@montebrisa.com' },
                { icon: 'ri-global-line', label: 'Website', value: 'www.Montebrisa.com', href: 'https://www.Montebrisa.com' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '+505 8508 8152', href: 'https://wa.me/50585088152' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+505-2772-0354', href: 'tel:+50527720354' },
              ].map(item => (
                <div key={item.label} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-cream-dark">
                  <div className="w-8 h-8 flex items-center justify-center bg-forest rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-white text-sm`} />
                  </div>
                  <div>
                    <p className="font-sans text-xs text-gray-400">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} rel="nofollow noopener noreferrer" target={item.href.startsWith('mailto') || item.href.startsWith('tel') ? '_self' : '_blank'} className="font-sans text-sm text-forest-dark font-medium hover:text-gold transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <p className="font-sans text-sm text-forest-dark font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Property photo */}
            <div className="mt-6 rounded-2xl overflow-hidden h-48">
              <img
                src="https://montebrisa.com/wp-content/uploads/slider/cache/c26d1ba1bfc58668f7e671b39c23e18d/inicio_6.jpg"
                alt="Montebrisa property"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          {/* Right — Form */}
          <div className="w-full lg:w-[60%] reveal" style={{ transitionDelay: '0.15s' }}>
            <div className="bg-white rounded-2xl border border-cream-dark p-7 md:p-9">
              <h3 className="font-serif text-2xl text-forest-dark mb-2">Confidential Investment Inquiry</h3>
              <p className="font-sans text-xs text-gray-400 mb-6">All submissions are strictly confidential and responded to within 24–48 hours.</p>

              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-forest-50 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-forest text-2xl" />
                  </div>
                  <p className="font-serif text-xl text-forest-dark mb-2">Inquiry Received</p>
                  <p className="font-sans text-sm text-gray-500">Thank you for your interest. We will be in contact within 24–48 hours with the requested information.</p>
                  <button onClick={() => setStatus('idle')} className="mt-5 px-5 py-2 border border-forest text-forest text-sm font-sans rounded-md hover:bg-forest hover:text-white transition-all cursor-pointer whitespace-nowrap">
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="montebrisa-booking-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
                      <input name="name" required type="text" placeholder="Your full name" className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors" />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Email Address *</label>
                      <input name="email" required type="email" placeholder="your@email.com" className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Phone / WhatsApp</label>
                      <input name="phone" type="tel" placeholder="+1 (555) 000-0000" className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors" />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Country of Residence</label>
                      <input name="country" type="text" placeholder="United States" className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors" />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">I am interested in *</label>
                    <select name="interest_type" required className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors cursor-pointer">
                      <option value="">Select an option</option>
                      <option>Purchasing the property &amp; business</option>
                      <option>Receiving the due diligence package</option>
                      <option>Scheduling a property visit</option>
                      <option>General investment inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-sans text-xs text-gray-500 uppercase tracking-wider mb-1.5 flex justify-between">
                      <span>Message / Questions</span>
                      <span className={charCount > 450 ? 'text-red-500' : 'text-gray-400'}>{charCount}/500</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Please share any specific questions, your investment background, or preferred timeline..."
                      onChange={e => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-2.5 bg-cream border border-cream-dark rounded-md text-sm font-sans focus:outline-none focus:border-forest transition-colors resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <input type="checkbox" name="nda_agreement" id="nda_agreement" required className="mt-1 cursor-pointer" />
                    <label htmlFor="nda_agreement" className="font-sans text-xs text-gray-500 cursor-pointer leading-relaxed">
                      I acknowledge that all information in this brochure is strictly confidential and agree not to share or reproduce it without prior written consent.
                    </label>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-xs font-sans">Something went wrong. Please try again or contact us directly.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full py-3 bg-forest text-white font-sans text-sm tracking-widest uppercase rounded-md hover:bg-forest-dark transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === 'sending' ? 'Sending...' : 'Submit Confidential Inquiry'}
                  </button>
                  <p className="text-center font-sans text-xs text-gray-400">Typically responded to within 24–48 hours</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
